import React, { Dispatch, KeyboardEvent, SetStateAction, useEffect, useRef } from 'react';
import styled, { useTheme } from 'styled-components';
// 커스텀 훅
import useSelect from 'hooks/useSelect';
import useCallbackRef from 'hooks/useCallbackRef';
// 아이콘
import CoreSvg from './CoreSvg';
import Icon from './icons';

type SelectType = 'common' | 'box';

interface ISelect {
	select_type: SelectType;
	default_quote?: string;
	option_list?: Array<string>;
	// 리스트의 값과 선택되었을 때 보여지는 값이 다를 때 사용
	selected_value?: string;
	selected_option?: string;
	setSelectedOption?: Dispatch<SetStateAction<string>> | ((value: string) => void);
	label?: string;
	_width?: string;
	_disabled?: boolean;
	_margin?: string;
	_onClick?: {
		void: ((param?: any) => void) | ((param: any) => void);
		index?: number[];
	};
	children?: JSX.Element;
}

const CoreSelect = ({
	select_type,
	default_quote = '선택하세요',
	option_list = ['Option 1', 'Option 2', 'Option 3', 'Option 4'],
	selected_value,
	selected_option,
	setSelectedOption,
	label,
	_width,
	_disabled = false,
	_margin,
	_onClick = { void: () => undefined },
	children,
}: ISelect) => {
	// select박스를 감싸는 label태그의 DOM
	const label_ref = useRef<HTMLDivElement>(null);

	// const ul_ref = useRef<HTMLUListElement>(null);
	const [ul_dom, ul_ref] = useCallbackRef();

	// 드롭다운이 열린 상태에서 외부 클릭 시, 드롭다운 닫기가 받영된 커스텀 훅
	const [click_selectbox, setClickSelectbox] = useSelect(label_ref);

	// select 클릭 시 드롭다운 열기
	const openSelect = () => {
		if (_disabled) return;
		setClickSelectbox(!click_selectbox);
	};

	// li 클릭 시 value 반영
	const handleSelect = (option: string) => {
		if (_disabled) return;
		if (setSelectedOption) {
			setSelectedOption(option);
		}
		setClickSelectbox(false);
	};

	const onKeyDownEvent = (idx: number, option: string, e: KeyboardEvent) => {
		if (!ul_dom || _disabled) return;

		const li_list = ul_dom?.getElementsByTagName('li');

		if (e.key === 'ArrowDown' && li_list.length - 1 !== idx) {
			li_list[idx + 1].focus();
		} else if (e.key === 'ArrowDown' && li_list.length - 1 === idx) {
			e.preventDefault();
			return;
		}

		if (e.key === 'ArrowUp' && idx !== 0) {
			li_list[idx - 1].focus();
		}

		if ((e.key === 'Enter' || e.key === ' ') && setSelectedOption) {
			setSelectedOption(option);
			setClickSelectbox(false);
		}
	};

	const setOptionPosition = () => {
		if (label_ref.current === null || ul_dom === null) return;

		const label_rect = label_ref.current.getBoundingClientRect();
		const label_bottom = window.innerHeight - label_rect.bottom;
		const label_top = label_rect.top;
		const option_num = children ? option_list.length + 1 : option_list.length;
		const option_item_height = 48;
		const _margin = 8;

		if (
			label_bottom < option_num * option_item_height &&
			label_top < option_num * option_item_height
		) {
			ul_dom.style.height = `${label_bottom - _margin - 32}px`;
			return 'down';
		} else if (label_bottom < option_list.length * option_item_height) return 'up';
		else return 'down';
	};

	const theme = useTheme();

	// sementic하게 구현하려고 했지만, 디자인 시안대로 커스텀하는 데에 한계가 있어 div로만 구현.
	return (
		<SelectBoxWrapper _width={_width} _margin={_margin}>
			{select_type === 'box' && label !== undefined && (
				<LabelforSelect>{label}</LabelforSelect>
			)}
			<SelectBox
				ref={label_ref}
				onMouseDown={openSelect}
				select_type={select_type}
				select_open={click_selectbox}
				_disabled={_disabled}
				onClick={(e) => e.stopPropagation()}
				tabIndex={0}
			>
				<SelectValue
					select_type={select_type}
					is_selected={selected_option && selected_option !== '' ? true : false}
					_disabled={_disabled}
				>
					{selected_option === '' || !selected_option
						? default_quote
						: selected_value ?? selected_option}
				</SelectValue>

				<SelectIconWrapper
					select_type={select_type}
					click_selectbox={click_selectbox}
					_disabled={_disabled}
				>
					<CoreSvg
						_fill={_disabled ? theme.colors.gray.gray30 : theme.colors.gray.gray80_text}
					>
						<Icon.ArrowDown />
					</CoreSvg>
				</SelectIconWrapper>

				{click_selectbox && (
					<OptionList
						select_type={select_type}
						ref={ul_ref}
						position={setOptionPosition()}
					>
						{children && (
							<OptionItem tabIndex={0} onMouseDown={(e) => e.stopPropagation()}>
								{children}
							</OptionItem>
						)}

						{option_list.map((option, option_index) => (
							<OptionItem
								tabIndex={0}
								key={option_index}
								onClick={() => {
									handleSelect(option);
									if (_onClick.index && _onClick.index.includes(option_index)) {
										_onClick.void(option);
									} else if (!_onClick.index) {
										_onClick.void(option);
									}
								}}
								onMouseDown={(e) => {
									e.stopPropagation();
								}}
								onKeyDown={(e) => {
									onKeyDownEvent(option_index, option, e);
								}}
							>
								{option}
							</OptionItem>
						))}
					</OptionList>
				)}
			</SelectBox>
		</SelectBoxWrapper>
	);
};

const SelectBoxWrapper = styled.div<{ _width?: string; _margin?: string }>`
	width: ${({ _width }) => _width ?? '100%'};
	${({ _margin }) => _margin && `margin: ${_margin}`};
	flex-shrink: 0;
	outline: none;
`;

const LabelforSelect = styled.div`
	white-space: nowrap;
	color: ${(props) => props.theme.colors.gray.gray_black};
	${({ theme }) => theme.typo.body.span};

	margin-bottom: 4px;
`;

const SelectBox = styled.div<{
	select_type: 'common' | 'box';
	select_open: boolean;
	_disabled: boolean;
}>`
	white-space: nowrap;
	position: relative;
	display: flex;
	align-items: center;
	outline: none;

	// type - common
	${(props) =>
		props.select_type === 'common' &&
		`
			padding: 16px;
			width: max-content;
			& > svg {
				margin-left: 4px;
			}
		`}

	// type - box
	${(props) =>
		props.select_type === 'box' &&
		`
			width: 100%;
			height: 56px;
			background-color: #fff;

			padding: 0 52px 0 16px;
			border-radius: 8px;
		`}
	${(props) =>
		props.select_type === 'box' &&
		props.select_open &&
		`border: 2px solid ${props.theme.colors.primary.primary50_key}`};
	${(props) =>
		props.select_type === 'box' &&
		!props.select_open &&
		`border: 1px solid ${props.theme.colors.gray.gray15}`};
	${({ select_type, _disabled, theme }) =>
		select_type === 'box' &&
		_disabled &&
		`background-color: ${theme.colors.gray.gray15};`};

	&:focus-visible {
		outline: none;
		border: ${(props) => `2px solid ${props.theme.colors.primary.primary50_key}`};
	}
`;

const SelectValue = styled.span<{
	select_type: 'common' | 'box';
	is_selected: boolean | undefined;
	_disabled: boolean;
}>`
	width: 100%;
	overflow: hidden;
	display: block;
	color: ${({ is_selected, theme }) =>
		is_selected ? theme.colors.gray.gray80_text : theme.colors.gray.gray20};

	${({ _disabled, theme }) => _disabled && `color: ${theme.colors.gray.gray30}`};

	${({ theme }) => theme.typo.body.p_bold};
	margin-right: 4px;
	width: ${(props) => (props.select_type === 'common' ? 'max-content' : '100%)')};

	${({ theme }) => theme.common.ellipsis};

	${({ _disabled }) => !_disabled && `cursor: pointer`};
`;

const SelectIconWrapper = styled.span<{
	select_type: 'common' | 'box';
	click_selectbox: boolean;
	_disabled: boolean;
}>`
	${({ _disabled }) => !_disabled && `cursor: pointer`};
	transform: ${(props) => (props.click_selectbox ? 'rotate(180deg)' : 'rotate(0deg)')};
	transition: 0.2s;

	${(props) =>
		props.select_type === 'box' &&
		`
			position: absolute;
			top: calc(50% - 12px);
			right: 12px;
			z-index: 15;
		`}
`;

const OptionList = styled.ul<{
	select_type: 'common' | 'box';
	position?: 'up' | 'down' | 'scroll';
}>`
	width: 100%;
	min-width: 80px;
	background-color: #fff;
	border: 1px solid ${(props) => props.theme.colors.gray.gray15};
	border-radius: 8px;
	box-shadow: ${(props) => props.theme.shadow.depth04};

	overflow-y: overlay;
	overflow-x: hidden;
	${({ theme }) => theme.common.scroll};

	position: absolute;
	left: -2px;
	top: ${({ position, select_type }) =>
		position === 'down' && select_type === 'common'
			? `calc(100% - 8px)`
			: position === 'down' && select_type === 'box'
			? `calc(100% + 8px)`
			: position === 'up' && select_type === 'common'
			? `8px`
			: position === 'up' && select_type === 'box' && `-8px`};
	${({ position }) => position === 'up' && `transform: translateY(-100%)`};

	z-index: 15;
`;

const OptionItem = styled.li`
	width: 100%;
	height: 48px;
	padding: 10px 16px;
	margin: 8px 0;

	color: ${(props) => props.theme.colors.gray.gray80_text};
	${({ theme }) => theme.typo.body.p};

	cursor: pointer;

	overflow: hidden;
	${({ theme }) => theme.common.ellipsis};
	line-height: 28px;

	&:hover {
		background-color: ${(props) => props.theme.colors.gray.gray05_bg};
	}
	&:focus-visible {
		outline: none;
		background-color: ${(props) => props.theme.colors.gray.gray05_bg};
	}
`;

export default CoreSelect;

// 사용방법
//
// 1. 이 컴포넌트를 임포트하는 상위 컴포넌트(이하 부모)에서 옵션의 리스트를 배열의 형태로 내려준다.
// ex) ['Poze', 'Harry', 'Jerry']
//
// 2. 부모는 이 컴포넌트의 선택된 값을 관리하는 state를 가지며 state 및 setState를 각각 selected_option과 setSelectedOption에 담아 이 컴포넌트로 내려준다.
//
// 그외의 props
// - select_type: common, box (common의 경우 박스가 없이 텍스트와 아이콘만 보이는 형태 - 피그마 참고)
// - default_quote: 선택하지 않았을 경우 보여지는 문구
// - label: 라벨 텍스트로 select_type이 box일 때만 사용된다.
